import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, CalendarDays, Loader2 } from "lucide-react";
import { CONTACT, INTERESTS } from "@/data/site";
import { Reveal, SectionHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", interest: INTERESTS[0], message: "" };

const field =
  "w-full bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-3 text-ivory placeholder:text-ivory/30 transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const { search } = useLocation();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const pre = new URLSearchParams(search).get("interest");
    if (pre && INTERESTS.includes(pre)) setForm((f) => ({ ...f, interest: pre }));
  }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — your inquiry has been received.", { description: "We'll be in touch shortly." });
      setForm(EMPTY);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(status < 500 && typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-40 overflow-hidden" data-testid="contact-section">
      <div className="pointer-events-none absolute left-[-10%] top-[10%] h-[560px] w-[560px] rounded-full bg-gold/10 blur-[170px]" />
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHead index="08" eyebrow="Contact" title={<>Start the <em className="not-italic text-gold">conversation.</em></>} />
          <Reveal delay={0.2}>
            <p className="mt-8 text-ivory/60 leading-relaxed max-w-md">
              Tell us about your goals, capital, or a property. We'll respond personally.
            </p>
          </Reveal>
          <Reveal delay={0.3} className="mt-12 space-y-6">
            <a href={`mailto:${CONTACT.email}`} className="group flex items-center gap-4 text-ivory/80 hover:text-gold transition-colors" data-testid="contact-email-link">
              <Mail size={16} strokeWidth={1.5} className="text-gold" />
              <span className="link-underline">{CONTACT.email}</span>
            </a>
            <a href={CONTACT.phoneHref} className="group flex items-center gap-4 text-ivory/80 hover:text-gold transition-colors" data-testid="contact-phone-link">
              <Phone size={16} strokeWidth={1.5} className="text-gold" />
              <span className="link-underline">{CONTACT.phone}</span>
            </a>
            <a href={CONTACT.calendly} target="_blank" rel="noreferrer" className="group flex items-center gap-4 text-ivory/80 hover:text-gold transition-colors" data-testid="contact-calendly-link">
              <CalendarDays size={16} strokeWidth={1.5} className="text-gold" />
              <span className="link-underline">Book a 15-minute introduction call</span>
              <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-8" data-testid="contact-form">
            <label className="block">
              <span className="eyebrow">Name</span>
              <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className={field} data-testid="contact-name-input" />
            </label>
            <label className="block">
              <span className="eyebrow">Email</span>
              <input required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={field} data-testid="contact-email-input" />
            </label>
            <label className="block">
              <span className="eyebrow">Phone</span>
              <input value={form.phone} onChange={set("phone")} placeholder="(optional)" className={field} data-testid="contact-phone-input" />
            </label>
            <label className="block">
              <span className="eyebrow">I'm interested in</span>
              <select value={form.interest} onChange={set("interest")} className={`${field} cursor-pointer [&>option]:bg-ink`} data-testid="contact-interest-select">
                {INTERESTS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="eyebrow">Message</span>
              <textarea required minLength={5} rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your goals or the property…" className={`${field} resize-none`} data-testid="contact-message-input" />
            </label>
            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-6 pt-2">
              <p className="text-xs text-ivory/35 max-w-xs leading-relaxed">
                No obligation. Submitting this form does not create an agreement or guarantee any outcome.
              </p>
              <button
                type="submit"
                disabled={busy}
                className="btn-gold inline-flex items-center gap-3 bg-gold text-obsidian text-xs tracking-[0.2em] uppercase px-8 py-4 disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {busy ? "Sending" : "Send inquiry"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
